"use client";

import { useState } from "react";
import { useLiveSession } from "@/contexts/live-session-context";
import { ShareInvite } from "./share-invite";

/**
 * Les commandes de récital que l'interprète a sous la main, où qu'il les
 * trouve : la prise, la trame du rouleau, et de quoi faire venir quelqu'un.
 *
 * Elles vivaient dans `LiveSessionPanel`, qui fut longtemps le seul endroit
 * d'où l'on pilote un récital. Le rouleau les porte maintenant en widgets
 * (`recital-widgets.tsx`), et le panneau en garde certaines pour le spectateur
 * et pour un live sans récital : deux écritures du même geste divergeraient au
 * premier ajustement, et c'est le genre de divergence qu'on ne voit qu'en la
 * cherchant.
 */

/**
 * La prise en cours, et le geste qui ouvre la suivante.
 *
 * # Pourquoi le numéro s'affiche
 *
 * Parce que sans lui, le découpage est une devinette. Quelqu'un qui
 * s'enregistre seul reprend la chanson du début jusqu'à la version qu'il
 * gardera ; savoir qu'il en est à la troisième pendant qu'il joue, c'est ce qui
 * lui dit que son signal a été reçu — et c'est la seule chose qui le lui dise,
 * puisque rien ne coupe le fichier à l'écran.
 *
 * Le numéro vient de l'état de séance, jamais d'un compteur local : c'est le
 * service qui tient l'autorité, et un chiffre avancé ici se ferait démentir par
 * la métadonnée suivante. Il arrive donc avec un temps de retard — celui de
 * l'aller-retour, plus l'amortissement des écritures du service. La frontière,
 * elle, ne prend pas ce retard : elle est datée à la position musicale du geste.
 *
 * # Pourquoi le bouton n'est pas toujours là
 *
 * Une prise découpe l'enregistrement de l'interprète, et le service refuse
 * (403) celle que demanderait quelqu'un d'autre. Le numéro, lui, reste visible :
 * il décrit la séance, pas un droit.
 *
 * Un clic double n'ouvre pas deux prises — le service reconnaît le rejeu à son
 * numéro (voir `sendNextTake`) —, ce qui est exactement pourquoi ce bouton peut
 * rester cliquable pendant que la métadonnée revient.
 */
export function TakeControl({
  take,
  mine,
  onNext,
}: {
  take: number;
  mine: boolean;
  onNext: () => Promise<boolean>;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const open = async () => {
    setBusy(true);
    setFailed(false);

    const opened = await onNext();

    setBusy(false);
    setFailed(!opened);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-10 items-center gap-2 rounded-lg bg-bg-elevated px-3">
        <span className="flex-1 text-[12px] font-medium text-text-primary">Prise {take}</span>

        {mine && (
          <button
            type="button"
            onClick={() => void open()}
            disabled={busy}
            className="h-7 shrink-0 px-2.5 rounded-md text-[11px] font-semibold text-text-secondary bg-white/[0.06] hover:bg-white/[0.1] hover:text-text-primary transition-colors duration-150 cursor-pointer disabled:cursor-default disabled:opacity-50"
          >
            {busy ? "· · ·" : "Nouvelle prise"}
          </button>
        )}
      </div>

      {failed && (
        <p role="alert" className="m-0 text-[12px] text-red-300">
          La prise n&apos;a pas été ouverte. Réessaie.
        </p>
      )}
    </div>
  );
}

/**
 * Les lignes de mesure du rouleau, le temps du récital.
 *
 * Pas un réglage du studio : devant un public, le rouleau est ce qu'on
 * regarde, et la trame y est un décor qu'on peut vouloir retirer — hors
 * récital, on la retrouve. Elle reste allumée quand le métronome la demande :
 * c'est alors la pulsation, pas un décor.
 *
 * # Ce qu'il règle, c'est l'écran de tout le monde
 *
 * Il ne l'a pas toujours fait : chacun tenait le sien, si bien que
 * l'interprète qui retirait la trame la retirait de son seul écran pendant que
 * le public continuait de la voir — soit exactement le contraire de ce que le
 * geste promet. Il passe donc par le service, qui publie le réglage dans
 * l'état de séance : la salle entière le reçoit d'un coup, et un spectateur
 * arrivé après le geste le trouve déjà posé, sans que personne ait à le lui
 * renvoyer.
 *
 * L'état affiché est celui de la séance, pas une bascule locale : si le geste
 * n'aboutit pas, l'interrupteur ne bouge pas — ce qui est la vérité, puisque
 * l'écran du public n'a pas bougé non plus. Le studio suit ensuite ce réglage
 * (voir `StudioPage`).
 *
 * Réservé à l'interprète : un spectateur ne décide pas de ce que la salle
 * regarde, et le service refuse son geste.
 */
export function MeasureLinesSwitch({ hidden, onChange }: { hidden: boolean; onChange: (hidden: boolean) => Promise<boolean> }) {
  const visible = !hidden;
  const setVisible = (next: boolean) => {
    void onChange(!next);
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={visible}
      onClick={() => setVisible(!visible)}
      className="flex w-full items-center justify-between gap-2 rounded-lg border border-border-subtle bg-white/[0.02] px-2.5 py-2 text-[12px] transition-colors duration-150 cursor-pointer hover:bg-white/[0.04]"
    >
      <span className={visible ? "text-text-primary font-medium" : "text-text-secondary"}>
        Lignes de mesure
      </span>

      <span
        aria-hidden
        className={`relative h-4 w-7 shrink-0 rounded-full transition-colors duration-150 ${
          visible ? "bg-accent/70" : "bg-white/15"
        }`}
      >
        <span
          className={`absolute top-0.5 size-3 rounded-full bg-white transition-all duration-150 ${
            visible ? "left-3.5" : "left-0.5"
          }`}
        />
      </span>
    </button>
  );
}


/**
 * De quoi faire venir quelqu'un dans un récital : le code s'il y en a un, et
 * le lien.
 *
 * # Le code, ici et nulle part ailleurs
 *
 * C'est le seul endroit du produit où il reste lisible une fois la fenêtre
 * d'ouverture refermée — et c'est précisément à ce moment-là qu'on en a besoin,
 * quand quelqu'un le redemande par message. L'API ne le rend qu'à qui a le
 * droit de le lire : sa présence suffit à décider, sans tester une deuxième
 * fois à qui appartient le récital.
 *
 * Celui d'un récital et celui d'une séance sont deux champs distincts, et il
 * n'y en a jamais deux à la fois : la salle d'un récital n'a pas de code à
 * elle. Le premier l'emporte, faute de quoi le studio d'un récital n'en
 * montrait aucun — ce qu'il a fait jusqu'au 2026-09-20.
 *
 * # Le dessin de la séance à plusieurs
 *
 * Le lien dans un champ, le code dans un encadré et la visibilité en
 * intertitre ont cédé la place au dessin de la séance à plusieurs
 * ({@link ShareInvite}), décidé le 2026-09-23 pour que les deux salles se
 * lisent pareil. La visibilité se lit dans la salle d'attente et sur les
 * cartes, où elle décide de quelque chose.
 */
export function RecitalInvite() {
  const live = useLiveSession();

  if (live === null || (live.session === null && live.shareUrl === null)) {
    return null;
  }

  const session = live.session;
  const code = session?.recital?.code ?? session?.code ?? null;

  return <ShareInvite code={code} shareUrl={live.shareUrl} copyShareUrl={live.copyShareUrl} />;
}
